import React, { useState } from "react";
import logo from "../../../assets/Aara_logo.png";
import PhoneInput from "react-phone-input-2";

const VisaStudentForm = ({
  formData,
  setFormData,
  handleSubmit,
  isSubmitted,
}) => {
  // Error state for form validation
  const [errors, setErrors] = useState({});
  // State to track if form was attempted to be submitted
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  // Comprehensive form validation function
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone || formData.phone.length < 10) {
      newErrors.phone = "Valid phone number is required";
    }

    if (
      !formData.residence_city_state ||
      formData.residence_city_state.trim() === ""
    ) {
      newErrors.residence_city_state = "Place of residence is required";
    }

    if (!formData.reference_source) {
      newErrors.reference_source = "Please select a reference source";
    } else {
      if (
        formData.reference_source === "Reference of Friend/Relative" &&
        (!formData.reference_name_id ||
          formData.reference_name_id.trim() === "")
      ) {
        newErrors.reference_name_id = "Referral name is required";
      }

      if (
        formData.reference_source === "Others" &&
        (!formData.other_reference_source ||
          formData.other_reference_source.trim() === "")
      ) {
        newErrors.other_reference_source =
          "Please specify the reference source";
      }
    }

    if (
      !formData.visa_country_name ||
      !Array.isArray(formData.visa_country_name) ||
      formData.visa_country_name.length === 0
    ) {
      newErrors.visa_country_name = "Please select at least one country";
    } else if (
      formData.visa_country_name.includes("Others") &&
      (!formData.other_country_visa ||
        formData.other_country_visa.trim() === "")
    ) {
      newErrors.other_country_visa = "Please specify other countries";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear validation error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));

    // Clear validation error for this field
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleAlternatePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      alternate_phone: value,
    }));
  };

  const handleReferenceChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: value,
      }));

      // Clear validation error for this field
      if (errors.reference_source) {
        setErrors((prev) => ({ ...prev, reference_source: "" }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: "", // Uncheck and clear the reference when the checkbox is unchecked
      }));
    }
  };

  const handleChangeCountry = (e) => {
    const { value, type } = e.target;

    setFormData((prevState) => {
      let selectedCountries = Array.isArray(prevState.visa_country_name)
        ? [...prevState.visa_country_name]
        : [];

      if (type === "checkbox") {
        if (value === "Others") {
          // Toggle "Others" in `country`, but do not store user input here
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
            return {
              ...prevState,
              visa_country_name: selectedCountries,
              other_country_visa: "",
            };
          } else {
            selectedCountries.push(value);
            return {
              ...prevState,
              visa_country_name: selectedCountries,
            };
          }
        } else {
          // Toggle regular country selection
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
          } else {
            selectedCountries.push(value);
          }
        }

        // Clear validation error if at least one country is selected
        if (selectedCountries.length > 0 && errors.visa_country_name) {
          setErrors((prev) => ({ ...prev, visa_country_name: "" }));
        }

        return {
          ...prevState,
          visa_country_name: selectedCountries,
        };
      } else {
        // Handle "Others" input text separately
        if (value && errors.other_country_visa) {
          setErrors((prev) => ({ ...prev, other_country_visa: "" }));
        }
        return { ...prevState, other_country_visa: value };
      }
    });
  };

  // Custom submit handler
  const onSubmit = (e) => {
    e.preventDefault();
    setFormSubmitAttempted(true);

    const formErrors = validateForm();
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      // If no errors, proceed with form submission
      handleSubmit(e);
    } else {
      // Scroll to first error
      const firstErrorField = document.querySelector(".error-message");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
        <img src={logo} alt="Aara Consultancy Logo" className="h-24 mb-6" />
        <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">
          Thank You for Submitting!
        </h1>
        <p className="text-lg text-gray-600 mb-6 text-center">
          We have received your information and will get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto p-4">
      {/* Logo Section */}
      <div className="flex justify-center mb-6">
        <img
          src={logo}
          alt="Aara Consultancy Logo"
          className="h-16 sm:h-20" // Responsive logo size
        />
      </div>
      <form
        onSubmit={onSubmit}
        className="bg-gray-100 shadow-lg p-4 sm:p-6 rounded-lg"
      >
        <h2 className="text-xl sm:text-2xl font-semibold text-center mb-4">
          Visa Student Information Form
        </h2>
        <p className="text-xs sm:text-sm text-gray-500 mb-4">
          Fields marked with * are mandatory and cannot be left blank!
        </p>

        <div className="space-y-4">
          {/* Name */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="name"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Name *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.name && formSubmitAttempted ? "border-red-500" : ""
                }`}
              />
              {errors.name && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.name}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="email"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Email *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.email && formSubmitAttempted ? "border-red-500" : ""
                }`}
              />
              {errors.email && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          {/* Mobile No */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="phone"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Mobile No *
            </label>
            <div className="w-full sm:w-1/2">
              <PhoneInput
                country={"in"} // Default country India (+91)
                value={formData.phone || ""}
                onChange={handlePhoneChange}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "6px",
                  borderColor:
                    errors.phone && formSubmitAttempted ? "#f56565" : "#ccc",
                  paddingLeft: "50px", // Adjust for flag
                }}
                containerStyle={{
                  width: "100%",
                }}
                dropdownStyle={{ width: "300px" }}
              />
              {errors.phone && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.phone}
                </p>
              )}
            </div>
          </div>

          {/* Alternate Contact Number */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="alternate_phone"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Alternate Contact Number
            </label>
            <div className="w-full sm:w-1/2">
              <PhoneInput
                country={"in"} // Default country India (+91)
                value={formData.alternate_phone || ""}
                onChange={handleAlternatePhoneChange}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "6px",
                  borderColor: "#ccc",
                  paddingLeft: "50px", // Adjust for flag
                }}
                containerStyle={{
                  width: "100%",
                }}
                dropdownStyle={{ width: "300px" }}
              />
            </div>
          </div>

          {/* Place of Residence */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="residence_city_state"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              In which State/City/Country do you reside in? *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="residence_city_state"
                id="residence_city_state"
                value={formData.residence_city_state || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.residence_city_state && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              />
              {errors.residence_city_state && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.residence_city_state}
                </p>
              )}
            </div>
          </div>

          {/* Reference Source */}
          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="reference_source"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              How did you get your reference? *
            </label>
            <div className="w-full md:w-1/2">
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-lg p-2 bg-white ${
                  formSubmitAttempted && errors.reference_source
                    ? "border-red-500"
                    : ""
                }`}
              >
                {[
                  "Reference of Friend/Relative",
                  "Word of Mouth",
                  "Google",
                  "Walk in",
                  "Seminar",
                  "Others",
                ].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      name="reference_source"
                      value={option}
                      onChange={handleReferenceChange}
                      checked={formData.reference_source === option}
                      className="mr-2"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.reference_source && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.reference_source}
                </p>
              )}
            </div>
          </div>
          {/* Conditional rendering based on selection */}
          {formData.reference_source === "Reference of Friend/Relative" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="reference_name_id"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                <span className="text-sm">
                  Please mention referral student name (Useful for them to claim
                  the referral amount)
                </span>
              </label>
              <div className="w-full sm:w-1/2">
                <input
                  type="text"
                  name="reference_name_id"
                  id="reference_name_id"
                  value={formData.reference_name_id || ""}
                  onChange={handleChange}
                  className={`border rounded-lg p-2 w-full  bg-white ${
                    formSubmitAttempted && errors.reference_name_id
                      ? "border-red-500"
                      : ""
                  }`}
                />
                {errors.reference_name_id && formSubmitAttempted && (
                  <p className="text-red-500 text-xs mt-1 error-message">
                    {errors.reference_name_id}
                  </p>
                )}
              </div>
            </div>
          )}

          {formData.reference_source === "Others" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="other_reference_source"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                If Others, Mention source of reference
              </label>
              <div className="w-full sm:w-1/2">
                <input
                  type="text"
                  name="other_reference_source"
                  id="other_reference_source"
                  value={formData.other_reference_source || ""}
                  onChange={handleChange}
                  className={`border rounded-lg p-2 w-full ${
                    errors.other_reference_source && formSubmitAttempted
                      ? "border-red-500"
                      : ""
                  }`}
                />
                {errors.other_reference_source && formSubmitAttempted && (
                  <p className="text-red-500 text-xs mt-1 error-message">
                    {errors.other_reference_source}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Country Name For Visa */}
          <div className="flex flex-col sm:flex-row sm:justify-between">
            <label
              htmlFor="visa_country_name"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Intended Countries (Multiple select) *
            </label>
            <div className="w-full sm:w-1/2">
              <div
                className={`grid grid-cols-2 gap-2 border rounded-lg p-2 bg-white ${
                  formSubmitAttempted && errors.visa_country_name
                    ? "border-red-500"
                    : ""
                }`}
              >
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="USA"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("USA")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">USA</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="UK"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("UK")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">UK</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Australia"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Australia")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Australia</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Canada"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Canada")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Canada</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Germany"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Germany")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Germany</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="India"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("India")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">India</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Ireland"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Ireland")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Ireland</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Newzealand"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Newzealand")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">New Zealand</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Others"
                    onChange={handleChangeCountry}
                    checked={
                      formData.visa_country_name &&
                      formData.visa_country_name.includes("Others")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Others</span>
                </label>
              </div>
              {errors.visa_country_name && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.visa_country_name}
                </p>
              )}
            </div>
          </div>

          {/* Conditionally render the input field if "Others" is checked */}
          {formData.visa_country_name &&
            formData.visa_country_name.includes("Others") && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <label
                  htmlFor="other_country_visa"
                  className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
                >
                  Other Countries For Visa (If Applicable)
                </label>
                <div className="w-full sm:w-1/2">
                  <input
                    type="text"
                    name="other_country_visa"
                    id="other_country_visa"
                    value={formData.other_country_visa || ""}
                    onChange={handleChangeCountry}
                    className={`border rounded-lg p-2 w-full ${
                      errors.other_country_visa && formSubmitAttempted
                        ? "border-red-500"
                        : ""
                    }`}
                  />
                  {errors.other_country_visa && formSubmitAttempted && (
                    <p className="text-red-500 text-xs mt-1 error-message">
                      {errors.other_country_visa}
                    </p>
                  )}
                </div>
              </div>
            )}

          {/* Submit Button */}
          <div className="flex justify-center mt-6">
            <button
              type="submit"
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VisaStudentForm;
